import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Footer } from '../components/Footer';
import { SponsorBanner } from '../components/SponsorBanner';

export const AboutBreed = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get tab from URL params
  const urlTab = searchParams.get('tab');
  
  const [activeTab, setActiveTab] = React.useState(urlTab || 'history');

  // Update active tab when URL changes
  useEffect(() => {
    if (urlTab) {
      setActiveTab(urlTab);
    }
  }, [urlTab]);

  const tabs = [
    { id: 'history', label: t.about.history.title },
    { id: 'brazil', label: t.about.brazil.title },
    { id: 'characteristics', label: t.about.characteristics.title },
    { id: 'colors', label: t.about.colors.title }
  ];

  // Function to render text with bold formatting
  const renderTextWithBold = (text) => {
    const parts = text.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index} className="font-bold text-brown-900">{part.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  };

  // Calculate percentages for pie charts
  const calculatePercentage = (value, total) => {
    return total > 0 ? (value / total) * 100 : 0;
  };

  const createPieChart = (data) => {
    let currentAngle = -90;
    return data.map((item, index) => {
      const percentage = calculatePercentage(item.value, data.reduce((sum, d) => sum + d.value, 0));
      const angle = (percentage / 100) * 360;
      const slice = {
        ...item,
        percentage,
        startAngle: currentAngle,
        endAngle: currentAngle + angle
      };
      currentAngle += angle;
      return slice;
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-semibold text-brown-600 mb-12 text-center">
            {t.about.title}
          </h1>

          {/* Custom Tabs */}
          <div className="mb-8">
            <div className="border-b border-gray-200">
              <nav className="flex flex-wrap gap-4 -mb-px">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`pb-2 px-1 border-b-2 font-medium text-base transition-colors ${
                      activeTab === tab.id
                        ? 'border-brown-900 text-brown-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-8">
              {/* Hero Image */}
              <div>
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    loading="lazy" decoding="async" src="https://customer-assets.emergentagent.com/job_elegant-ocicat/artifacts/0horxtao_perth-selvaggem.jpeg"
                    alt="Perth"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-right italic">Foto: Carla Durante</p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">{t.about.history.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-gray-600 leading-relaxed text-lg">
                    {t.about.history.content.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4 last:mb-0">
                        {renderTextWithBold(paragraph)}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Timeline */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl">Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    {/* Vertical line for desktop */}
                    <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-brown-900 hidden md:block"></div>
                    
                    {/* Timeline items */}
                    <div className="space-y-6">
                      {t.about.history.timeline.map((item, index) => (
                        <div key={index} className="relative">
                          <div className="flex items-start gap-6">
                            {/* Year badge */}
                            <div className="flex-shrink-0 w-16 md:w-20 relative">
                              <div className="bg-brown-900 text-white px-3 py-2 rounded-lg text-center font-bold text-sm md:text-base">
                                {item.year}
                              </div>
                              {/* Connecting line for mobile */}
                              <div className="absolute left-full top-1/2 w-6 h-0.5 bg-brown-900 md:hidden"></div>
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 pb-6">
                              <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-gray-700 leading-relaxed mb-4">{item.event}</p>
                                
                                {/* Image if exists */}
                                {item.image && (
                                  <div className="mt-3">
                                    {item.imageLink ? (
                                      <a
                                        href={item.imageLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`history-timeline-image-link-${index}`}
                                      >
                                        <img
                                          loading="lazy" decoding="async" src={item.image}
                                          alt={`${item.year} - ${item.event}`}
                                          className="w-full max-w-xs h-auto rounded-lg shadow-md object-cover transition-transform hover:scale-[1.02]"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      </a>
                                    ) : (
                                      <img
                                        loading="lazy" decoding="async" src={item.image}
                                        alt={`${item.year} - ${item.event}`}
                                        className="w-full max-w-xs h-auto rounded-lg shadow-md object-cover"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Brazil Tab */}
          {activeTab === 'brazil' && (
            <div className="space-y-8">
              {/* Hero Image */}
              <div>
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    loading="lazy" decoding="async" src="https://customer-assets.emergentagent.com/job_pedigree-cats/artifacts/xgjg1urj_ocicat-geracao.JPG"
                    alt="Ocicat Gerações"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-right italic">Foto: Carla Durante</p>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">{t.about.brazil.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-gray-600 leading-relaxed text-lg">
                    {t.about.brazil.content.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4 last:mb-0">
                        {renderTextWithBold(paragraph)}
                      </p>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Charts Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">Dados Demográficos</CardTitle>
                    <span className="text-sm text-gray-500">{t.about.brazil.charts.dataDate}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-12">
                    {/* Bar Chart - Births */}
                    <div>
                      <h3 className="text-lg font-semibold text-brown-900 mb-6">{t.about.brazil.charts.births.title}</h3>
                      <div className="space-y-4">
                        {t.about.brazil.charts.births.data.map((item, index) => (
                          <div key={index}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-brown-900 font-medium">{item.year}</span>
                              <span className="text-brown-900 font-semibold">{item.count}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-8">
                              <div
                                className="bg-brown-900 h-8 rounded-full flex items-center justify-end px-3 transition-all duration-500"
                                style={{ width: `${(item.count / 10) * 100}%` }}
                              >
                                <span className="text-white text-sm font-medium">{item.count}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                        {t.about.brazil.charts.births.total && (
                          <div className="pt-3 border-t border-gray-300 flex justify-between">
                            <span className="text-brown-900 font-bold">Total</span>
                            <span className="text-brown-900 font-bold">{t.about.brazil.charts.births.total}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pie Charts Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {/* Population Pie Chart */}
                      <div>
                        <h3 className="text-lg font-semibold text-brown-900 mb-6">{t.about.brazil.charts.population.title}</h3>
                        <div className="flex flex-col items-center">
                          <div className="relative w-48 h-48 mb-6">
                            <svg viewBox="0 0 100 100" className="transform -rotate-90">
                              {createPieChart(t.about.brazil.charts.population.data).map((slice, index) => {
                                const x1 = 50 + 40 * Math.cos((slice.startAngle * Math.PI) / 180);
                                const y1 = 50 + 40 * Math.sin((slice.startAngle * Math.PI) / 180);
                                const x2 = 50 + 40 * Math.cos((slice.endAngle * Math.PI) / 180);
                                const y2 = 50 + 40 * Math.sin((slice.endAngle * Math.PI) / 180);
                                const largeArc = slice.percentage > 50 ? 1 : 0;
                                
                                return (
                                  <path
                                    key={index}
                                    d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                                    fill={slice.color}
                                  />
                                );
                              })}
                            </svg>
                          </div>
                          <div className="space-y-2 w-full">
                            {t.about.brazil.charts.population.data.map((item, index) => (
                              <div key={index} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color }}></div>
                                  <span className="text-gray-700">{item.label}</span>
                                </div>
                                <span className="text-brown-900 font-semibold">{item.value}</span>
                              </div>
                            ))}
                            <div className="pt-2 border-t border-gray-200 flex justify-between">
                              <span className="text-gray-700 font-medium">Total</span>
                              <span className="text-brown-900 font-bold">{t.about.brazil.charts.population.total}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Location Pie Chart */}
                      <div>
                        <h3 className="text-lg font-semibold text-brown-900 mb-6">{t.about.brazil.charts.location.title}</h3>
                        <div className="flex flex-col items-center">
                          <div className="relative w-48 h-48 mb-6">
                            <svg viewBox="0 0 100 100" className="transform -rotate-90">
                              {createPieChart(t.about.brazil.charts.location.data.filter(d => d.value > 0)).map((slice, index) => {
                                const x1 = 50 + 40 * Math.cos((slice.startAngle * Math.PI) / 180);
                                const y1 = 50 + 40 * Math.sin((slice.startAngle * Math.PI) / 180);
                                const x2 = 50 + 40 * Math.cos((slice.endAngle * Math.PI) / 180);
                                const y2 = 50 + 40 * Math.sin((slice.endAngle * Math.PI) / 180);
                                const largeArc = slice.percentage > 50 ? 1 : 0;
                                
                                return (
                                  <path
                                    key={index}
                                    d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                                    fill={slice.color}
                                  />
                                );
                              })}
                            </svg>
                          </div>
                          <div className="space-y-2 w-full">
                            {t.about.brazil.charts.location.data.map((item, index) => (
                              <div key={index} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color }}></div>
                                  <span className="text-gray-700">{item.label}</span>
                                </div>
                                <span className="text-brown-900 font-semibold">{item.value}</span>
                              </div>
                            ))}
                            {t.about.brazil.charts.location.total && (
                              <div className="pt-2 border-t border-gray-200 flex justify-between">
                                <span className="text-brown-900 font-bold">Total</span>
                                <span className="text-brown-900 font-bold">{t.about.brazil.charts.location.total}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Timeline - Ocicats no Brasil */}
              {t.about.brazil.timeline && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-xl">Timeline de Conquistas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="relative">
                      {/* Vertical line for desktop */}
                      <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-brown-900 hidden md:block"></div>
                      
                      {/* Timeline items */}
                      <div className="space-y-6">
                        {t.about.brazil.timeline.map((item, index) => (
                          <div key={index} className="relative">
                            <div className="flex items-start gap-6">
                              {/* Year badge */}
                              <div className="flex-shrink-0 w-16 md:w-20 relative">
                                <div className="bg-brown-900 text-white px-3 py-2 rounded-lg text-center font-bold text-sm md:text-base">
                                  {item.year}
                                </div>
                                {/* Connecting line for mobile */}
                                <div className="absolute left-full top-1/2 w-6 h-0.5 bg-brown-900 md:hidden"></div>
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 pb-6">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                  <p className="text-gray-700 leading-relaxed mb-4">{item.event}</p>
                                  
                                  {/* Single Image */}
                                  {item.image && (
                                    <div className="mt-3">
                                      {item.imageLink ? (
                                        <a
                                          href={item.imageLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          data-testid={`timeline-image-link-${index}`}
                                        >
                                          <img
                                            loading="lazy" decoding="async" src={item.image}
                                            alt={`${item.year} - ${item.event}`}
                                            className="w-full max-w-sm h-auto rounded-lg shadow-md object-cover transition-transform hover:scale-[1.02]"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        </a>
                                      ) : (
                                        <img
                                          loading="lazy" decoding="async" src={item.image}
                                          alt={`${item.year} - ${item.event}`}
                                          className="w-full max-w-sm h-auto rounded-lg shadow-md object-cover"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Multiple Images */}
                                  {item.images && (
                                    <div className="mt-3 grid grid-cols-2 gap-3">
                                      {item.images.map((img, imgIdx) => (
                                        <img
                                          loading="lazy" decoding="async" key={imgIdx}
                                          src={img}
                                          alt={`${item.year} - ${item.event} ${imgIdx + 1}`}
                                          className="w-full h-auto rounded-lg shadow-md object-cover"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Characteristics Tab */}
          {activeTab === 'characteristics' && (
            <div className="space-y-8">
              {t.about.characteristics.items.map((item, index) => {
                const isEven = index % 2 === 0;
                const hasImage = item.image;
                
                return (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <div className={`flex flex-col ${hasImage ? 'md:flex-row' : ''} gap-6 ${!isEven && hasImage ? 'md:flex-row-reverse' : ''}`}>
                        {/* Image Section - Only if image exists */}
                        {hasImage && (
                          <div className="w-full md:w-1/3 flex-shrink-0">
                            <div className="aspect-square rounded-xl overflow-hidden shadow-lg">
                              <img
                                loading="lazy" decoding="async" src={item.image}
                                alt={item.title}
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          </div>
                        )}
                        
                        {/* Text Section */}
                        <div className={`flex flex-col justify-center ${hasImage ? 'md:w-2/3' : 'w-full'}`}>
                          <h3 className="text-xl font-bold text-brown-900 mb-4">{item.title}</h3>
                          <p className="text-gray-600 leading-relaxed text-lg">
                            {renderTextWithBold(item.description)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}

              {/* Final Hero Image - Perth */}
              <div className="mt-8">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    loading="lazy" decoding="async" src="https://customer-assets.emergentagent.com/job_feline-mostacada/artifacts/rd1flbxo_perth-selvagem2.jpeg"
                    alt="Perth - Ocicat"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-right italic">Foto: Carla Durante</p>
              </div>

              {/* CTA Section - Want an Ocicat */}
              <div className="py-16 px-6 bg-brown-900 text-white rounded-2xl mt-8 text-center">
                <h2 className="text-4xl font-bold mb-6">
                  {language === 'pt' ? 'Gostaria de ter um Ocicat?' : 'Would you like to have an Ocicat?'}
                </h2>
                <p className="text-xl mb-8 text-gray-200">
                  {language === 'pt' 
                    ? 'Descubra como você pode fazer parte da família Ocicat Brasil'
                    : 'Discover how you can become part of the Ocicat Brasil family'}
                </p>
                <button
                  onClick={() => {
                    navigate('/cattery?tab=how');
                    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
                  }}
                  className="inline-block px-10 py-4 bg-white text-brown-900 rounded-full hover:bg-gray-100 transition-all duration-200 text-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  {language === 'pt' ? 'Saiba Mais' : 'Learn More'}
                </button>
              </div>
            </div>
          )}

          {/* Colors Tab */}
          {activeTab === 'colors' && (
            <div className="space-y-6">
              {/* Hero Image - Filhotes */}
              <div>
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    loading="lazy" decoding="async" src="https://customer-assets.emergentagent.com/job_pedigree-cats/artifacts/uovr5aif_filhotes3.JPG"
                    alt="Filhotes Ocicat"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-right italic">Foto: Carla Durante</p>
              </div>

              {/* Introduction */}
              <Card>
                <CardContent className="pt-6">
                  <p className="text-gray-600 leading-relaxed text-lg mb-4">
                    {renderTextWithBold(t.about.colors.intro)}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                    {t.about.colors.introList.map((item, index) => (
                      <li key={index}>{renderTextWithBold(item)}</li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Sections */}
              {t.about.colors.sections.map((section, index) => (
                <Card key={index}>
                  <CardHeader>
                    <CardTitle className="text-xl text-brown-900">{section.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="text-gray-600 leading-relaxed">
                      {section.content.split('\n').map((line, idx) => (
                        <p key={idx} className={line.trim() ? 'mb-3' : 'mb-1'}>
                          {renderTextWithBold(line)}
                        </p>
                      ))}
                    </div>

                    {/* Subsection for colors */}
                    {section.subsection && (
                      <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-semibold text-brown-900 mb-2">{section.subsection.title}</h4>
                        <p className="text-sm text-gray-500 mb-3">{section.subsection.subtitle}</p>
                        <div className="space-y-2">
                          {section.subsection.items.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-brown-900 rounded-full"></div>
                              <span className="text-gray-700">{item}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-gray-600 mt-4 italic">{section.subsection.footer}</p>
                      </div>
                    )}

                    {/* Bases */}
                    {section.bases && (
                      <div className="space-y-4 mt-6">
                        {section.bases.map((base, idx) => (
                          <div key={idx} className="p-4 border border-gray-200 rounded-lg">
                            <h4 className="font-bold text-brown-900 mb-2">{base.name}</h4>
                            <div className="text-gray-600">
                              {base.description.split('\n').map((line, lineIdx) => (
                                <p key={lineIdx} className={line.trim() ? 'mb-2 last:mb-0' : ''}>
                                  {renderTextWithBold(line)}
                                </p>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Patterns */}
                    {section.patterns && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                        {/* Warm patterns */}
                        <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <h4 className="font-bold text-brown-900 mb-4">{section.patterns.warm.title}</h4>
                          <div className="space-y-2">
                            {section.patterns.warm.items.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <span className="flex-shrink-0 w-6 h-6 bg-brown-900 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                  {idx + 1}
                                </span>
                                <span className="text-gray-700">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Silver patterns */}
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-300">
                          <h4 className="font-bold text-brown-900 mb-4">{section.patterns.silver.title}</h4>
                          <div className="space-y-2">
                            {section.patterns.silver.items.map((item, idx) => (
                              <div key={idx} className="flex items-center gap-3">
                                <span className="flex-shrink-0 w-6 h-6 bg-brown-900 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                  {idx + 7}
                                </span>
                                <span className="text-gray-700">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Sponsor Banner - Only on History and Brazil tabs */}
      {(activeTab === 'history' || activeTab === 'brazil') && (
        <SponsorBanner language={language} />
      )}

      <Footer />
    </div>
  );
};